import React, { useState, useMemo, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { matchPath, useHistory } from "react-router-dom";
import BaseModal from "../base_modal_v2";
import { Button, Col, Grid, Row } from "react-bootstrap";
import "../campaign_schedule_podcast/campaign_podcast_schedule.scss";
import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import mapValues from "lodash/mapValues";
import { If } from "react-extras";
import {
  createCampaignItems,
  updateCampaignItems,
  getCampaign,
} from "../../../action_managers/campaigns";
import closeIcon from "../../../icons/modal-close-icon-white.svg";
import { AddToCampaign, renderToContext } from "../../lib/tables/campaign_show_list_table";
import LoadingButton from "../../forms/loading_button/loading_button";
import Spinner from "../../lib/spinner";
import AdvertiserSearch from "../../lib/show_search/advertiser_search";
import { validateCampaign } from "../../../lib/validate-campaign";

export const PodcastSelector = ({
  selectedShows,
  setSelectedShows,
  campaign,
  disabledShows,
  search,
}) => {
  if (!campaign) {
    return <Spinner isLoading={true} />;
  }
  const onSelect = show =>
    setSelectedShows({
      ...selectedShows,
      [show.uuid]: (selectedShows[show.uuid] ?? 0) | 0b01,
    });
  const onRemove = show =>
    setSelectedShows({
      ...selectedShows,
      [show.uuid]: selectedShows[show.uuid] & 0b10,
    });
  const onInviteAll = () => {
    setSelectedShows(
      search.reduce(
        (agg, { uuid }) => {
          if (disabledShows.has(uuid)) return agg;

          agg[uuid] = (agg[uuid] ?? 0) | 0b01;
          return agg;
        },
        { ...selectedShows }
      )
    );
  };

  const onRemoveAll = () => {
    setSelectedShows(
      search.reduce(
        (agg, { uuid }) => {
          if (disabledShows.has(uuid)) return agg;
          agg[uuid] = (agg[uuid] ?? 0) & 0b10;
          return agg;
        },
        { ...selectedShows }
      )
    );
  };
  const InviteRemoveAll = filters => {
    const hasFilters = ![
      "categoryUUIDs",
      "weeklyDownloads",
      "CPM",
      "episodeDownloads",
      "spotRate",
    ].some(key => filters[key]);
    if (!search.length || hasFilters) {
      return null;
    }
    if (search.every(({ uuid }) => selectedShows[uuid] && selectedShows[uuid] & 0b01)) {
      return (
        <button className="fake-link" onClick={onRemoveAll}>
          Remove All
        </button>
      );
    }

    return (
      <button className="fake-link" onClick={onInviteAll}>
        Invite All
      </button>
    );
  };
  const inviteColumn = {
    titleMapper: InviteRemoveAll,
    render: renderToContext(
      <AddToCampaign
        onClick={onSelect}
        onRemove={onRemove}
        selectedShows={selectedShows}
        disabledShows={disabledShows}
      />
    ),
    align: "right",
  };

  return (
    <div className={"campaign-podcast-selector height-100 p-bxxl black-text"}>
      <Row>
        <Col xs={12} md={10} mdOffset={1}>
          <AdvertiserSearch
            campaign={campaign}
            additionalColumns={[inviteColumn]}
            additionalFilters={{
              ...(campaign?.style === "hostRead" && { isHostReadEnabled: true }),
              ...(campaign?.style === "campaignAudio" && { isPreRecordedEnabled: true }),
            }}
          />
        </Col>
      </Row>
      <If
        condition={
          !isEmpty(selectedShows) &&
          Object.keys(selectedShows).some(showUUID => selectedShows[showUUID] & 0b1)
        }>
        <div className={"selection-indicator"}>
          {Object.keys(selectedShows).filter(showUUID => selectedShows[showUUID] & 0b1).length}{" "}
          Added to Campaign
          <img
            src={closeIcon}
            alt="close"
            className={"cursor-pointer"}
            onClick={() => setSelectedShows(mapValues(selectedShows, value => value & 0b10))}
          />
        </div>
      </If>
    </div>
  );
};

const CampaignPodcastSelectorModal = props => {
  const history = useHistory();

  const match = matchPath(window.location.pathname, {
    path: "/campaigns/:campaignUUID/select",
  });

  const campaignUUID = match?.params.campaignUUID;
  const campaign = props.campaigns[campaignUUID];
  const items = campaign?.items;
  const showsUUIDsAlreadyInCampaign = useMemo(
    () =>
      (items ?? []).reduce((agg, { item }) => {
        if (item.state === "draft") {
          agg[item.showUUID] = 0b11;
        }
        return agg;
      }, {}),
    [items]
  );

  const showUUIDToItemMap = useMemo(
    () =>
      keyBy(
        (items ?? []).map(({ item }) => item),
        "showUUID"
      ),
    [items]
  );

  // shows that have already been sent, completed etc... only editable state is 'draft'
  const disabledShows = useMemo(
    () =>
      (items ?? []).reduce((agg, { item }) => {
        if (item.state !== "draft") {
          agg.add(item.showUUID);
        }
        return agg;
      }, new Set()),
    [items]
  );
  /*
    In code code below, for selectedShows, first bit denote whether it's in the backend, and the second bit denotes
    frontend state.

    BE FE Action
    0  0    no action
    0  1   create
    1  0   delete
    1  1   no action
  */
  const [selectedShows, setSelectedShows] = useState(showsUUIDsAlreadyInCampaign);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!campaign) {
      dispatch(getCampaign(campaignUUID)).then(
        resp => resp.status === 200 && setSelectedShows(showsUUIDsAlreadyInCampaign)
      );
    } else {
      setSelectedShows(showsUUIDsAlreadyInCampaign);
    }
  }, [campaignUUID, showsUUIDsAlreadyInCampaign, dispatch, campaign]);

  const close = () => {
    props.closeModal();
    history.push(props.closeRoute(window.location));
  };

  const submit = () => {
    if (isEmpty(selectedShows)) {
      props.closeModal();
    }
    const isFirstTime = (campaign.items ?? []).every(({ item }) => item.state === "draft");

    const { updateRequest, createRequests } = Object.keys(selectedShows).reduce(
      (agg, showUUID) => {
        switch (selectedShows[showUUID]) {
          case 0b10: // exists on backend, now we're deleting it
            agg.updateRequest[showUUIDToItemMap[showUUID].uuid] = { delete: true };
            break;
          case 0b01: // doesn't exist on backend, now we're adding
            agg.createRequests.push({
              showUUID,
              startAt: campaign?.startsAt,
            });
            break;
          default:
        }
        return agg;
      },
      {
        createRequests: [],
        updateRequest: {},
      }
    );
    const promises = [];
    if (createRequests.length) {
      promises.push(props.createCampaignItems(campaignUUID, createRequests));
    }

    if (!isEmpty(updateRequest)) {
      promises.push(props.updateCampaignItems(campaignUUID, updateRequest));
    }
    Promise.all(promises).then(resps => {
      if (resps.every(resp => resp.status === 200)) {
        const hasSelectedPodcast = Object.entries(selectedShows).some(([, v]) => v & 0b1);
        if (isFirstTime && hasSelectedPodcast && validateCampaign(campaign)[0]) {
          props.closeModal();
          history.push(`/campaigns/${campaignUUID}/schedule`);
          return;
        }
        close();
      }
    });
  };

  return (
    <BaseModal
      title="Select Podcasts"
      subTitle={
        "We have hundreds of Podcasts to choose from. You can filter or search to help narrow your selection."
      }
      close={close}
      Content={
        <PodcastSelector
          {...{ selectedShows, setSelectedShows, campaign, disabledShows, search: props.search }}
        />
      }
      fillScreen
      showContent
      isLarge
      Footer={
        <Grid fluid>
          <Row>
            <Col mdOffset={1} md={5} xs={6}>
              <Button
                className="btn-secondary btn-large import-modal__submit-button"
                onClick={close}>
                Cancel
              </Button>
            </Col>
            <Col md={5} xs={6}>
              <LoadingButton
                isLoading={props.isLoading}
                Button={Button}
                className="btn-primary btn-large import-modal__submit-button"
                onClick={submit}>
                Save
              </LoadingButton>
            </Col>
          </Row>
        </Grid>
      }
    />
  );
};
export default connect(
  state => ({
    campaigns: state.campaigns.campaigns,
    isLoading: state.campaignItems.isLoading,
    search: state.browsePromotion.search.results,
  }),
  { createCampaignItems, updateCampaignItems, getCampaign }
)(CampaignPodcastSelectorModal);
